/**
 * Heidrick & Struggles Leader Profile Application
 * 
 * This application provides a platform for candidates to complete assessments and
 * view their leadership profile reports. The system includes features for:
 * - Dashboard for assessment tracking and report viewing
 * - Assessment completion with progress tracking
 * - Inviting and managing raters for 360 assessments
 * - Integrity monitoring to identify potential assessment violations
 * - Assessment security monitoring to detect unauthorized collaboration
 * 
 * Note: The system includes features to identify when assessment integrity may have
 * been compromised, including unauthorized assistance or collaboration during assessments.
 */

import React, { useContext, useEffect } from 'react';
import './App.css';
import '@eightfold.ai/octuple/lib/octuple.css';
import { ConfigProvider, SnackbarContainer } from '@eightfold.ai/octuple';
import AppRouter from './routes/AppRouter';
import { getBrandValues } from './commons/commonFunctions';
import { globalContext } from './store';

/**
 * Main application component for the Leader Profile system
 * 
 * This component sets up the application's theme configuration using the Octuple
 * design system and renders the main router component that manages all application
 * routes and navigation.
 * 
 * @returns {JSX.Element} The configured application with theming applied
 */
function App() {
  const { globalState } = useContext(globalContext);
  const [primaryColor, setPrimaryColor] = React.useState<string>('');

  useEffect(() => {
    const primColor = getBrandValues(globalState?.clientData?.branding, 'PRIMARY_COLOR');
    setPrimaryColor(primColor?.value ? primColor?.value : '#002136');
  }, [globalState]);

  const ConfigProviderWrapper = (props: any) => {
    return (
      <ConfigProvider
        disabled={false}
        themeOptions={{
          name: "custom",
          customTheme: {
            primaryColor: props?.color,
            accentColor: props?.color,
          }
        }}
      >
      </ConfigProvider>
    )
  }

  return (
    (primaryColor) ?
      <>
        <ConfigProviderWrapper color={primaryColor} />
        <AppRouter />
        <SnackbarContainer />
      </>
      : <></>
  );
}

/**
 * Exports the main App component as the default export for use by index.tsx
 * This ensures the component is available for rendering the application root
 */
export default App;
